// src/app/App.js

import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { MainLayout } from "./layout/MainLayout";

import { LoginUser } from "../auth/loginUser";
import { Register } from "../auth/Register";

import { TownBudget } from "../features/townBudget/components/townBudget";
import { SeecReports } from "../features/seec/components/SeecReports";
import { SeecDonorCard } from "../features/seec/components/SeecDonorCard";
import { SeecTopDonorsPage } from "../features/seec/components/SeecTopDonorsPage";
import { HomePage } from "../features/home/HomePage";
import { BoardOfEducation } from "../features/boe/components/BoardOfEducation";
import { BudgetVsSpent } from "../features/budgetVsSpent/components/BudgetVsSpent";
import { TaxDensityMap } from "../features/taxDensityMap/components/TaxDensityMap";
import { ElectionsPage } from "../features/elections/ElectionsPage";
import { CrimeDashboard } from "../features/crime/components/CrimeDashboard";

import { America250EventsPage } from "../misc/america250/America250EventsPage";
import { SeoRouteMeta } from "../shared/seo/SeoRouteMeta";

const RentDataPage = React.lazy(() => import("../features/rentData/RentDataPage"));

const PropertyResearchPage = React.lazy(() => import("../features/propertyResearch/PropertyResearchPage"));

const PropertiesPage = React.lazy(() => import("../features/properties/PropertiesPage"));

const TrafficDensityPage = React.lazy(() => import("../features/trafficDensity/TrafficDensityPage"));
const SchoolPopulationMap = React.lazy(() => import("../features/boe/components/SchoolPopulationMap"));
const ResidentialDensityMap = React.lazy(() => import("../features/boe/components/ResidentialDensityMap"));

function BudgetComparisonPage() {
  const location = useLocation();
  const context = (new URLSearchParams(location.search).get('context') || location.state?.budgetContext) === 'boe' ? 'boe' : 'town';
  return <BudgetVsSpent key={context} context={context}/>;
}

function App() {
  return (
    <BrowserRouter>
      <SeoRouteMeta />
      <Routes>
        {/* Shared Layout route */}
        <Route path="/" element={<MainLayout />}>
          {/* Home Page */}
          <Route index element={<HomePage />} />

          {/* Town Budget */}
          <Route path="budget" element={<TownBudget />} />
          <Route path="town" element={<TownBudget />} />

          {/* Maps opens the first map; existing report URLs remain shareable. */}
          <Route path="maps" element={<Navigate to="/tax-density-map" replace />} />

          {/* Tax Density Map */}
          <Route path="traffic-density" element={<React.Suspense fallback={<p>Loading traffic map…</p>}><TrafficDensityPage /></React.Suspense>} />
          <Route path="properties/rent/:view?" element={<React.Suspense fallback={<p>Loading rent data…</p>}><RentDataPage /></React.Suspense>} />
          <Route path="properties/types" element={<React.Suspense fallback={<p>Loading properties…</p>}><PropertiesPage /></React.Suspense>} />
          <Route path="properties/research" element={<React.Suspense fallback={<p>Loading property research…</p>}><PropertyResearchPage /></React.Suspense>} />
          <Route path="properties/reports" element={<Navigate to="/properties/reports/values" replace />} />
          <Route path="properties/reports/:reportView" element={<React.Suspense fallback={<p>Loading properties…</p>}><PropertiesPage /></React.Suspense>} />
          <Route path="properties/map" element={<React.Suspense fallback={<p>Loading properties…</p>}><PropertiesPage /></React.Suspense>} />
          <Route path="properties/taxes" element={<React.Suspense fallback={<p>Loading properties…</p>}><PropertiesPage /></React.Suspense>} />
          <Route path="properties/:propertyId?" element={<React.Suspense fallback={<p>Loading properties…</p>}><PropertiesPage /></React.Suspense>} />
          <Route path="tax-density-map" element={<TaxDensityMap />} />

          {/* Crime and police activity */}
          <Route path="crime" element={<CrimeDashboard />} />

          {/* BoE */}
          <Route path="reports" element={<BoardOfEducation />} />
          <Route path="boe" element={<BoardOfEducation />} />
          <Route path="boe/maps" element={<React.Suspense fallback={<p>Loading school map…</p>}><SchoolPopulationMap /></React.Suspense>} />
          <Route path="boe/maps/residences" element={<React.Suspense fallback={<p>Loading residential map…</p>}><ResidentialDensityMap /></React.Suspense>} />
          <Route path="reports/maps" element={<Navigate to="/boe/maps" replace />} />

          <Route path="elections" element={<ElectionsPage />} />

          {/* Budget vs Spent */}
          <Route path="budget-vs-spent" element={<BudgetComparisonPage />} />

          {/* Auth Pages */}
          <Route path="login" element={<LoginUser />} />
          <Route path="register" element={<Register />} />

          {/* SEEC Reports */}
          <Route
            path="seec"
            element={<SeecReports />}
          />
          <Route
            path="seec/donor/:docId"
            element={<SeecDonorCard />}
          />
          <Route
            path="seec/top-donors"
            element={<SeecTopDonorsPage />}
          />

          {/* America 250 */}
          <Route path="250" element={<America250EventsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
